import React from "react";
import dayjs from "dayjs";
import { Alert, Box, TextField, Typography } from "@mui/material";
import SectionCrudPage from "./SectionCrudPage";
import {
  CrmCheckboxInput,
  CrmDateInput,
  CrmFormGrid,
  CrmSelectInput,
  CrmTextInput,
} from "../../components/crm/CrudFormControls";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import { resolveMediaUrl } from "../../utils/backendUrl";

const WEEKDAY_OPTIONS = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" },
];

const WEEKDAY_LABEL_BY_VALUE = Object.fromEntries(
  WEEKDAY_OPTIONS.map((option) => [String(option.value), option.label])
);

const getWeekdayLabel = (value) => WEEKDAY_LABEL_BY_VALUE[String(value)] || String(value || "-");

const normalizeRelation = (row, key) => {
  if (!row || typeof row !== "object") return [];
  const snake = row[key] || [];
  if (Array.isArray(snake)) return snake;
  const camelKey = key
    .split("_")
    .map((part, index) => (index === 0 ? part : `${part[0]?.toUpperCase() || ""}${part.slice(1)}`))
    .join("");
  return Array.isArray(row[camelKey]) ? row[camelKey] : [];
};

const normalizeIdArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item && typeof item === "object" ? item.id : item))
    .filter((item) => item !== null && item !== undefined && String(item).trim() !== "")
    .map((item) => String(item));
};

const normalizeSelectedIds = (value) => {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(
    value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
  ));
};

const toPositiveInt = (value) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return null;
  const normalized = Math.floor(parsed);
  return normalized > 0 ? normalized : null;
};

const resolveWeekOneMonday = (startDate) => {
  const start = dayjs(startDate);
  if (!start.isValid()) return null;

  // dayjs().day(): 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const dayOfWeek = start.day();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  return start.add(mondayOffset, "day");
};

const buildAssignmentCalendarPreview = (startDate, planDays) => {
  const weekOneMonday = resolveWeekOneMonday(startDate);
  if (!weekOneMonday || !Array.isArray(planDays) || !planDays.length) return [];

  return [...planDays]
    .map((day) => ({
      id: day?.id,
      week_number: toPositiveInt(day?.week_number) || 1,
      day_number: toPositiveInt(day?.day_number) || 1,
      title: String(day?.title || "").trim(),
      focus: String(day?.focus || "").trim(),
    }))
    .sort((a, b) => (a.week_number - b.week_number) || (a.day_number - b.day_number))
    .map((day) => {
      const offsetDays = ((day.week_number - 1) * 7) + (day.day_number - 1);
      const date = weekOneMonday.add(offsetDays, "day");
      return {
        ...day,
        date_iso: date.format("YYYY-MM-DD"),
        date_label: date.format("ddd, DD MMM YYYY"),
      };
    });
};

const buildExerciseFormFromRow = (row) => {
  const muscles = normalizeRelation(row, "muscle_groups");
  const relatedEquipments = normalizeRelation(row, "equipments");
  const relatedPatterns = normalizeRelation(row, "movement_patterns");
  const relatedCategories = normalizeRelation(row, "categories");

  const targetMusclesFromRelation = muscles
    .filter((muscle) => String(muscle?.pivot?.role || "") === "target")
    .map((muscle) => muscle.id);
  const secondaryMusclesFromRelation = muscles
    .filter((muscle) => String(muscle?.pivot?.role || "") === "secondary")
    .map((muscle) => muscle.id);

  const targetMuscles = targetMusclesFromRelation.length
    ? normalizeIdArray(targetMusclesFromRelation)
    : normalizeIdArray(row?.target_muscle_group_ids || []);
  const secondaryMuscles = secondaryMusclesFromRelation.length
    ? normalizeIdArray(secondaryMusclesFromRelation)
    : normalizeIdArray(row?.secondary_muscle_group_ids || []);
  const equipments = normalizeIdArray(
    relatedEquipments.length
      ? relatedEquipments
      : (row?.equipment_ids || [])
  );
  const patterns = normalizeIdArray(
    relatedPatterns.length
      ? relatedPatterns
      : (row?.movement_pattern_ids || [])
  );
  const categories = normalizeIdArray(
    relatedCategories.length
      ? relatedCategories
      : (row?.category_ids || [])
  );

  return {
    ...row,
    target_muscle_group_ids: targetMuscles,
    secondary_muscle_group_ids: secondaryMuscles,
    equipment_ids: equipments,
    movement_pattern_ids: patterns,
    category_ids: categories,
  };
};

const mapExercisePayload = (formData) => {
  const payload = {
    name: String(formData.name || "").trim(),
    slug: String(formData.slug || "").trim(),
    description: formData.description || null,
    difficulty: formData.difficulty || "beginner",
    status: formData.status || "active",
    is_system: Boolean(formData.is_system),
    target_muscle_group_ids: normalizeSelectedIds(formData.target_muscle_group_ids),
    secondary_muscle_group_ids: normalizeSelectedIds(formData.secondary_muscle_group_ids),
    equipment_ids: normalizeSelectedIds(formData.equipment_ids),
    movement_pattern_ids: normalizeSelectedIds(formData.movement_pattern_ids),
    category_ids: normalizeSelectedIds(formData.category_ids),
  };

  if (!payload.description) {
    delete payload.description;
  }

  return payload;
};

function TrainingPlanAssignmentsForm({
  formData,
  setFormData,
  errors,
  clearFieldError,
  selectOptions,
  parentRow,
}) {
  const selectedPlanId = String(formData?.training_plan_id || parentRow?.id || "").trim();
  const [planDays, setPlanDays] = React.useState([]);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState("");

  React.useEffect(() => {
    const parentPlanId = String(parentRow?.id || "").trim();
    const currentPlanId = String(formData?.training_plan_id || "").trim();

    if (!parentPlanId || currentPlanId) return;

    setFormData((previous) => ({
      ...previous,
      training_plan_id: parentPlanId,
    }));
    clearFieldError?.("training_plan_id");
  }, [parentRow?.id, formData?.training_plan_id, setFormData, clearFieldError]);

  React.useEffect(() => {
    let active = true;

    const loadPlanDays = async () => {
      if (!selectedPlanId) {
        setPlanDays([]);
        setPreviewError("");
        setPreviewLoading(false);
        return;
      }

      setPreviewLoading(true);
      setPreviewError("");

      const response = await callBackendApi(`/training-plans/${selectedPlanId}`, "GET");
      if (!active) return;

      if (response?.success) {
        const days = Array.isArray(response?.data?.days) ? response.data.days : [];
        setPlanDays(days);
        setPreviewError("");
      } else {
        setPlanDays([]);
        setPreviewError("Could not load plan structure for calendar preview.");
      }

      setPreviewLoading(false);
    };

    loadPlanDays();

    return () => {
      active = false;
    };
  }, [selectedPlanId]);

  const previewRows = React.useMemo(
    () => buildAssignmentCalendarPreview(formData?.start_date, planDays),
    [formData?.start_date, planDays]
  );

  return (
    <>
      <CrmFormGrid>
        <CrmSelectInput
          name="training_plan_id"
          label="Plan"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          options={selectOptions.training_plan_id || []}
          disabled={Boolean(parentRow?.id)}
        />
        <CrmSelectInput
          name="gym_user_id"
          label="Gym User"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          options={selectOptions.gym_user_id || []}
        />
        <CrmSelectInput
          name="trainer_user_id"
          label="Trainer"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          options={selectOptions.trainer_user_id || []}
          nullable
        />
        <CrmSelectInput
          name="status"
          label="Status"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          options={[
            { value: "active", label: "Active" },
            { value: "paused", label: "Paused" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
          ]}
        />
        <CrmDateInput
          name="start_date"
          label="Start Date"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
        />
        <CrmDateInput
          name="end_date"
          label="End Date"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
        />
        <CrmDateInput
          name="valid_until"
          label="Valid Until"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
        />
        <CrmTextInput
          name="notes"
          label="Notes"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
        />
      </CrmFormGrid>

      <Box sx={{ mt: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Calendar Preview
        </Typography>

        {!formData?.start_date ? (
          <Typography variant="body2" color="text.secondary">
            Select a start date to preview projected workout dates.
          </Typography>
        ) : previewLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading plan structure...
          </Typography>
        ) : previewError ? (
          <Alert severity="warning">{previewError}</Alert>
        ) : previewRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No plan days available yet for preview.
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {previewRows.map((item) => (
              <li key={`${item.id || "day"}-${item.week_number}-${item.day_number}`}>
                <Typography variant="body2">
                  {item.date_label} · Week {item.week_number} · {getWeekdayLabel(item.day_number)}
                  {item.title ? ` · ${item.title}` : ""}
                </Typography>
              </li>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

function ExerciseMediaAssetsForm({
  formData,
  setFormData,
  errors,
  clearFieldError,
}) {
  const [localPreviewUrl, setLocalPreviewUrl] = React.useState("");

  React.useEffect(() => {
    const selectedFile = formData?.file;
    if (!(selectedFile instanceof File)) {
      setLocalPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setLocalPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [formData?.file]);

  const persistedPreviewUrl = String(formData?.public_url || "").trim();
  const fallbackStoragePathUrl = formData?.storage_path
    ? `/storage/${String(formData.storage_path).replace(/^\/+/, "")}`
    : "";
  const persistedResolvedUrl = resolveMediaUrl(fallbackStoragePathUrl || persistedPreviewUrl);
  const versionToken = String(formData?.updated_at || formData?.id || "").trim();
  const persistedPreviewWithVersion = persistedResolvedUrl && versionToken
    ? `${persistedResolvedUrl}${persistedResolvedUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(versionToken)}`
    : persistedResolvedUrl;
  const previewUrl = localPreviewUrl || persistedPreviewWithVersion;

  const mediaType = String(formData?.media_type || "").toLowerCase();
  const selectedFileMime = String(formData?.file?.type || "").toLowerCase();
  const persistedMime = String(formData?.file_mime || "").toLowerCase();
  const isVideo = mediaType === "video" || selectedFileMime.startsWith("video/") || persistedMime.startsWith("video/");
  const fileError = errors?.file || "";

  return (
    <>
      <CrmFormGrid>
        <CrmSelectInput
          name="media_type"
          label="Media Type"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          options={[
            { value: "image", label: "Image" },
            { value: "gif", label: "GIF" },
            { value: "video", label: "Video" },
          ]}
        />
        <CrmSelectInput
          name="variant"
          label="Variant"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          options={[
            { value: "original", label: "Original" },
            { value: "thumb", label: "Thumb" },
            { value: "small", label: "Small" },
            { value: "medium", label: "Medium" },
            { value: "large", label: "Large" },
          ]}
        />
        <CrmCheckboxInput
          name="is_primary"
          label="Primary Media"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
        />
        <Box sx={{ gridColumn: "1 / -1" }}>
          <TextField
            fullWidth
            type="file"
            label="Upload File"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              accept: "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/x-msvideo,video/x-m4v",
            }}
            onChange={(event) => {
              const nextFile = event.target.files?.[0] || null;
              setFormData((prev) => ({ ...prev, file: nextFile }));
              clearFieldError?.("file");
            }}
            error={Boolean(fileError)}
            helperText={fileError || "Accepted: image, gif, or video"}
          />
        </Box>
      </CrmFormGrid>

      <Box sx={{ mt: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Preview
        </Typography>
        {!previewUrl ? (
          <Typography variant="body2" color="text.secondary">
            No media selected yet.
          </Typography>
        ) : isVideo ? (
          <video
            src={previewUrl}
            controls
            preload="metadata"
            style={{ width: "100%", maxWidth: 420, borderRadius: 8, display: "block" }}
          />
        ) : (
          <img
            src={previewUrl}
            alt="Exercise media preview"
            style={{ width: "100%", maxWidth: 420, maxHeight: 260, objectFit: "contain", borderRadius: 8, display: "block" }}
          />
        )}
      </Box>
    </>
  );
}

const trainingPlanDaysResource = {
  key: "training-plan-days",
  label: "Plan Days",
  endpoint: "/training-plan-days",
  tableFields: [
    { name: "training_plan_id", label: "Plan", valueGetter: (row) => row?.training_plan?.name || row?.trainingPlan?.name || row?.training_plan_id },
    { name: "week_number", label: "Week" },
    { name: "day_number", label: "Day", valueGetter: (row) => getWeekdayLabel(row?.day_number) },
    { name: "title", label: "Title" },
    { name: "focus", label: "Focus" },
  ],
  relatedTabs: [
    { key: "training-plan-day-exercises", label: "Day Exercises", endpoint: "/training-plan-day-exercises", foreignKey: "training_plan_day_id" },
  ],
  formFields: [
    {
      name: "training_plan_id",
      label: "Training Plan",
      type: "select",
      optionSource: { endpoint: "/training-plans", valueField: "id", labelFields: ["name", "difficulty"] },
    },
    { name: "week_number", label: "Week Number", type: "number", defaultValue: 1 },
    {
      name: "day_number",
      label: "Weekday",
      type: "select",
      defaultValue: 1,
      options: WEEKDAY_OPTIONS,
    },
    { name: "title", label: "Title", type: "text", nullable: true },
    { name: "focus", label: "Focus", type: "text", nullable: true },
  ],
};

const trainingPlanDayExercisesResource = {
  key: "training-plan-day-exercises",
  label: "Day Exercises",
  endpoint: "/training-plan-day-exercises",
  tableFields: [
    {
      name: "training_plan_day_id",
      label: "Plan Day",
      valueGetter: (row) => {
        const day = row?.training_plan_day || row?.trainingPlanDay;
        if (!day) return row?.training_plan_day_id;
        const dayLabel = getWeekdayLabel(day?.day_number);
        return `W${day?.week_number || "?"} · ${dayLabel}${day?.title ? ` · ${day.title}` : ""}`;
      },
    },
    { name: "exercise_id", label: "Exercise", valueGetter: (row) => row?.exercise?.name || row?.exercise_id },
    { name: "display_order", label: "Order" },
    { name: "sets", label: "Sets" },
    { name: "reps", label: "Reps" },
    { name: "rest_seconds", label: "Rest (s)" },
    { name: "tempo", label: "Tempo" },
    { name: "rpe", label: "RPE" },
  ],
  formFields: [
    {
      name: "training_plan_day_id",
      label: "Plan Day",
      type: "select",
      optionSource: { endpoint: "/training-plan-days", valueField: "id", labelFields: ["title", "week_number", "day_number"] },
    },
    {
      name: "exercise_id",
      label: "Exercise",
      type: "select",
      optionSource: { endpoint: "/exercises?status=active", valueField: "id", labelFields: ["name", "slug"] },
    },
    { name: "display_order", label: "Display Order", type: "number", defaultValue: 1 },
    { name: "sets", label: "Sets", type: "text", nullable: true },
    { name: "reps", label: "Reps", type: "text", nullable: true },
    { name: "rest_seconds", label: "Rest Seconds", type: "number", nullable: true },
    { name: "tempo", label: "Tempo", type: "text", nullable: true },
    { name: "rpe", label: "RPE", type: "number", nullable: true },
    { name: "notes", label: "Notes", type: "text", nullable: true },
  ],
};

const trainingPlanAssignmentsResource = {
  key: "training-plan-assignments",
  label: "Assignments",
  endpoint: "/training-plan-assignments",
  createInitialFormData: () => ({
    status: "active",
  }),
  tableFields: [
    { name: "training_plan_id", label: "Plan", valueGetter: (row) => row?.training_plan?.name || row?.trainingPlan?.name || row?.training_plan_id },
    { name: "gym_user_id", label: "Gym User", valueGetter: (row) => row?.gym_user?.name || row?.gymUser?.name || row?.gym_user_id },
    { name: "trainer_user_id", label: "Trainer", valueGetter: (row) => row?.trainer?.name || row?.trainer_user_id },
    { name: "start_date", label: "Start Date" },
    { name: "end_date", label: "End Date" },
    { name: "valid_until", label: "Valid Until" },
    { name: "status", label: "Status" },
  ],
  formFields: [
    {
      name: "training_plan_id",
      label: "Plan",
      type: "select",
      optionSource: { endpoint: "/training-plans", valueField: "id", labelFields: ["name", "difficulty"] },
    },
    {
      name: "gym_user_id",
      label: "Gym User",
      type: "select",
      optionSource: { endpoint: "/users?userType=CUSTOMER", valueField: "id", labelFields: ["name", "email"] },
    },
    {
      name: "trainer_user_id",
      label: "Trainer",
      type: "select",
      nullable: true,
      optionSource: { endpoint: "/users?userType=EMPLOYEE", valueField: "id", labelFields: ["name", "email"] },
    },
    { name: "start_date", label: "Start Date", type: "date" },
    { name: "end_date", label: "End Date", type: "date", nullable: true },
    { name: "valid_until", label: "Valid Until", type: "date", nullable: true },
    {
      name: "status",
      label: "Status",
      type: "select",
      defaultValue: "active",
      options: [
        { value: "active", label: "Active" },
        { value: "paused", label: "Paused" },
        { value: "completed", label: "Completed" },
        { value: "cancelled", label: "Cancelled" },
      ],
    },
    { name: "notes", label: "Notes", type: "text", nullable: true },
  ],
  selectSources: [
    {
      name: "training_plan_id",
      optionSource: { endpoint: "/training-plans", valueField: "id", labelFields: ["name", "difficulty"] },
    },
    {
      name: "gym_user_id",
      optionSource: { endpoint: "/users?userType=CUSTOMER", valueField: "id", labelFields: ["name", "email"] },
    },
    {
      name: "trainer_user_id",
      optionSource: { endpoint: "/users?userType=EMPLOYEE", valueField: "id", labelFields: ["name", "email"] },
    },
  ],
  renderForm: TrainingPlanAssignmentsForm,
};

const exerciseMediaAssetsResource = {
  key: "exercise-media-assets",
  label: "Media Assets",
  endpoint: "/exercise-media-assets",
  tableFields: [
    {
      name: "exercise_id",
      label: "Exercise",
      valueGetter: (row) => row?.exercise?.name || row?.exercise_id,
    },
    { name: "media_type", label: "Type" },
    { name: "variant", label: "Variant" },
    {
      name: "public_url",
      label: "Media URL",
      valueGetter: (row) => row?.public_url || "",
    },
    { name: "is_primary", label: "Primary", valueGetter: (row) => (row?.is_primary ? "Yes" : "No") },
    { name: "file_mime", label: "MIME" },
    { name: "file_size", label: "Size (bytes)" },
  ],
  createInitialFormData: () => ({
    media_type: "image",
    variant: "original",
    is_primary: false,
    file: null,
  }),
  renderForm: ExerciseMediaAssetsForm,
  toPayload: (formData) => {
    const payload = {
      media_type: String(formData?.media_type || "image").trim(),
      variant: String(formData?.variant || "original").trim(),
      is_primary: Boolean(formData?.is_primary),
    };

    if (formData?.exercise_id) {
      payload.exercise_id = formData.exercise_id;
    }

    if (formData?.file instanceof File) {
      payload.file = formData.file;
    }

    return payload;
  },
};

const resourceLookup = {
  "exercise-media-assets": exerciseMediaAssetsResource,
  "training-plan-days": trainingPlanDaysResource,
  "training-plan-day-exercises": trainingPlanDayExercisesResource,
  "training-plan-assignments": trainingPlanAssignmentsResource,
};

const resources = [
  {
    key: "exercises",
    label: "Exercises",
    endpoint: "/exercises",
    tableFields: [
      { name: "name", label: "Name" },
      { name: "slug", label: "Slug" },
      { name: "difficulty", label: "Difficulty" },
      { name: "status", label: "Status" },
      { name: "is_system", label: "System", valueGetter: (row) => (row?.is_system ? "Yes" : "No") },
      {
        name: "primary_media",
        label: "Primary Media",
        valueGetter: (row) => {
          const media = (row?.media_assets || []).find((asset) => asset?.is_primary);
          return media?.public_url || "";
        },
      },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      { name: "slug", label: "Slug", type: "text" },
      {
        name: "difficulty",
        label: "Difficulty",
        type: "select",
        defaultValue: "beginner",
        options: [
          { value: "beginner", label: "Beginner" },
          { value: "intermediate", label: "Intermediate" },
          { value: "advanced", label: "Advanced" },
        ],
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        defaultValue: "active",
        options: [
          { value: "active", label: "Active" },
          { value: "archived", label: "Archived" },
        ],
      },
      { name: "is_system", label: "System Exercise", type: "boolean", defaultValue: false },
      { name: "description", label: "Description", type: "text", nullable: true },
      {
        name: "target_muscle_group_ids",
        label: "Target Muscles",
        type: "select",
        multiple: true,
        defaultValue: [],
        optionSource: { endpoint: "/muscle-groups", valueField: "id", labelFields: ["name", "slug"] },
      },
      {
        name: "secondary_muscle_group_ids",
        label: "Secondary Muscles",
        type: "select",
        multiple: true,
        defaultValue: [],
        optionSource: { endpoint: "/muscle-groups", valueField: "id", labelFields: ["name", "slug"] },
      },
      {
        name: "equipment_ids",
        label: "Equipments",
        type: "select",
        multiple: true,
        defaultValue: [],
        optionSource: { endpoint: "/equipments", valueField: "id", labelFields: ["name", "slug"] },
      },
      {
        name: "movement_pattern_ids",
        label: "Movement Patterns",
        type: "select",
        multiple: true,
        defaultValue: [],
        optionSource: { endpoint: "/movement-patterns", valueField: "id", labelFields: ["name", "slug"] },
      },
      {
        name: "category_ids",
        label: "Categories",
        type: "select",
        multiple: true,
        defaultValue: [],
        optionSource: { endpoint: "/exercise-categories", valueField: "id", labelFields: ["name", "slug"] },
      },
    ],
    mapRowToFormData: buildExerciseFormFromRow,
    toPayload: mapExercisePayload,
    relatedTabs: [
      {
        key: "exercise-media-assets",
        label: "Media Assets",
        endpoint: "/exercise-media-assets",
        foreignKey: "exercise_id",
      },
    ],
  },
  {
    key: "training-plans",
    label: "Plans",
    endpoint: "/training-plans",
    tableFields: [
      { name: "name", label: "Name" },
      { name: "difficulty", label: "Difficulty" },
      { name: "duration_weeks", label: "Weeks" },
      { name: "default_validity_days", label: "Default Validity (Days)" },
      { name: "status", label: "Status" },
      { name: "trainer_user_id", label: "Trainer", valueGetter: (row) => row?.trainer?.name || row?.trainer_user_id },
    ],
    relatedTabs: [
      { key: "training-plan-days", label: "Plan Days", endpoint: "/training-plan-days", foreignKey: "training_plan_id" },
      {
        key: "training-plan-day-exercises",
        label: "Day Exercises",
        endpoint: "/training-plan-day-exercises",
        fromTab: "training-plan-days",
        fromField: "id",
        toField: "training_plan_day_id",
      },
      { key: "training-plan-assignments", label: "Assignments", endpoint: "/training-plan-assignments", foreignKey: "training_plan_id" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      { name: "goal", label: "Goal", type: "text", nullable: true },
      {
        name: "difficulty",
        label: "Difficulty",
        type: "select",
        defaultValue: "beginner",
        options: [
          { value: "beginner", label: "Beginner" },
          { value: "intermediate", label: "Intermediate" },
          { value: "advanced", label: "Advanced" },
        ],
      },
      { name: "duration_weeks", label: "Duration (Weeks)", type: "number", defaultValue: 4 },
      { name: "default_validity_days", label: "Default Validity Days", type: "number", nullable: true },
      {
        name: "status",
        label: "Status",
        type: "select",
        defaultValue: "draft",
        options: [
          { value: "draft", label: "Draft" },
          { value: "active", label: "Active" },
          { value: "archived", label: "Archived" },
        ],
      },
      {
        name: "trainer_user_id",
        label: "Trainer",
        type: "select",
        nullable: true,
        optionSource: { endpoint: "/users?userType=EMPLOYEE", valueField: "id", labelFields: ["name", "email"] },
      },
    ],
  },
  {
    key: "muscle-groups",
    label: "Muscle Groups",
    endpoint: "/muscle-groups",
    tableFields: [
      { name: "name", label: "Name" },
      { name: "slug", label: "Slug" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      { name: "slug", label: "Slug", type: "text", nullable: true },
    ],
  },
  {
    key: "equipments",
    label: "Equipments",
    endpoint: "/equipments",
    tableFields: [
      { name: "name", label: "Name" },
      { name: "slug", label: "Slug" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      { name: "slug", label: "Slug", type: "text", nullable: true },
    ],
  },
  {
    key: "movement-patterns",
    label: "Movement Patterns",
    endpoint: "/movement-patterns",
    tableFields: [
      { name: "name", label: "Name" },
      { name: "slug", label: "Slug" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      { name: "slug", label: "Slug", type: "text", nullable: true },
    ],
  },
  {
    key: "exercise-categories",
    label: "Exercise Categories",
    endpoint: "/exercise-categories",
    tableFields: [
      { name: "name", label: "Name" },
      { name: "slug", label: "Slug" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      { name: "slug", label: "Slug", type: "text", nullable: true },
    ],
  },
];

export default function TrainingPage() {
  return (
    <SectionCrudPage
      title="Training"
      subtitle="Manage exercises, plans, and training taxonomies."
      resources={resources}
      resourceLookup={resourceLookup}
    />
  );
}
